import { QuantityField } from 'components/Common/UnitEditor/QuantityField'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, { useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  QuantityDto,
  QuantityDtoType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { projectSelectors } from '../store/selectors/projectSelectors'

type Props = {
  bomItemPointer: BomItemPointer
  activityId: string
  onChange: (value: QuantityDto) => void
  disabled?: boolean
}

export type BomItemMaterialDiscountPercentageRef = {
  reset: () => void
}

export const BomItemMaterialDiscountPercentage = React.forwardRef(
  (
    props: Props,
    ref: React.ForwardedRef<BomItemMaterialDiscountPercentageRef>,
  ) => {
    const { t } = useTranslation()

    const bomItemDiscount = useAppSelector(
      projectSelectors.bomItemMaterialFinancialSelector(
        props.bomItemPointer,
        props.activityId,
        'discountRatio',
      ),
    ) as QuantityDto

    const [discountRatio, setDiscountRatio] = useState<QuantityDto>({
      value: bomItemDiscount?.value ?? 0,
      unit: '%',
      quantityType: QuantityDtoType.Ratio,
    })

    useEffect(() => {
      if (bomItemDiscount) {
        setDiscountRatio(bomItemDiscount)
      }
    }, [bomItemDiscount])

    const [validations, setValidations] = useState<string | undefined>()

    const handleChange = (newValue: QuantityDto) => {
      if (newValue.value > 100) {
        setValidations(t('common:max-value', { maxValue: 100 }))
        return
      }

      if (newValue.value < 0) {
        setValidations(t('common:min-value', { minValue: 0 }))
        return
      }

      setValidations(undefined)
      setDiscountRatio(newValue)

      props.onChange(newValue)
    }

    useImperativeHandle(ref, () => ({
      reset: () => {
        setDiscountRatio({
          value: bomItemDiscount?.value ?? 0,
          unit: '%',
          quantityType: QuantityDtoType.Ratio,
        })
      },
    }))

    return (
      <>
        <QuantityField
          name="discountRatio"
          label={`${t('common:discount')}`}
          value={discountRatio}
          onChange={(value) => handleChange(value as QuantityDto)}
          disabled={props.disabled}
          helperText={validations}
          error={Boolean(validations)}
        />
      </>
    )
  },
)
